import React, { useReducer } from "react";
import { hot } from "react-hot-loader";
import Fuse from "fuse.js";
import "react-rangeslider/lib/index.css";
import round from "lodash/round";
import findIndex from "lodash/findIndex";
import SearchResults from "./SearchResults";
import AdvancedSearchForm from "./AdvancedSearchForm";
import { ACTION_TYPES } from "./ActionTypes";
import {
  snapTo,
  getSearchConfig,
  getInitialState,
  parseDf,
  parseDk,
} from "./utils";

const fuse = new Fuse(window.isolaData.products, {
  includeScore: false,
  useExtendedSearch: true,
  keys: [
    "title",
    "excerpt",
    "content",
    "regions",
    "attributes.slug",
    "markets.slug",
    "slashsheets",
  ],
});

const primaryMenuItems = [
  { title: "All Products" },
  {
    title: "By Attribute",
    facets: window.isolaData.attributes,
  },
  {
    title: "By Market",
    facets: window.isolaData.markets,
  },
  { title: "Advanced Search" },
];

const initialState = getInitialState();

function init(state) {
  const searchConfig = getSearchConfig(state);
  if (searchConfig.$and) {
    state.searchResults = fuse.search(searchConfig);
  } else {
    state.searchResults = initialState.searchResults;
  }
  state.searchResults = state.searchResults.filter((r) => {
    const product = r.item || r;
    // ex: df: "0.0028 - 0.0035"
    // ex: dk: "3.38 / 3.45 / 3.60 / 3.75"
    // value <= dk & df
    // value >= tg & td
    const { tg, td, dk, df } = state;
    const dfs = parseDf(product.df);
    const maxDf = Math.max(...dfs);
    const minDf = Math.min(...dfs);

    const dks = parseDk(product.dk);
    const maxDk = Math.max(...dks);
    const minDk = Math.min(...dks);

    return (
      (maxDf <= df || minDf <= df) &&
      (maxDk <= dk || minDk <= dk) &&
      parseFloat(product.tg) >= tg &&
      parseFloat(product.td) >= td
    );
  });

  return state;
}

function reducer(state, action) {
  const newState = { ...state };
  switch (action.type) {
    case ACTION_TYPES.SET_PRIMARY_MENU:
      newState.primaryMenu = action.payload;
      break;
    case ACTION_TYPES.SET_SECONDARY_MENU:
      newState.secondaryMenu = action.payload;
      break;
    case ACTION_TYPES.RESET_SEARCH_RESULTS:
      newState.searchResults = initialState.searchResults;
      newState.searchText = initialState.searchText;
      newState.searchConfig = initialState.searchConfig;
      newState.attributes = initialState.attributes;
      newState.markets = initialState.markets;
      newState.regions = initialState.regions;
      newState.slashsheets = initialState.slashsheets;
      newState.tg = initialState.tg;
      newState.td = initialState.td;
      newState.df = initialState.df;
      return newState;
    case ACTION_TYPES.SET_SEARCH_RESULTS:
      newState.searchResults = action.payload;
      return newState;
    case ACTION_TYPES.SET_SEARCH_TEXT:
      newState.searchText = action.payload;
      break;
    case ACTION_TYPES.TOGGLE_CHECKBOX_BY_ID_TYPE:
      newState[action.payload.key] = state[action.payload.key].map((a) => ({
        ...a,
        checked: action.payload.id === a.id ? !a.checked : a.checked,
      }));
      break;
    case ACTION_TYPES.TOGGLE_ALL_CHECKBOX_TYPE:
      newState[action.payload.key] = state[action.payload.key].map((a) => ({
        ...a,
        checked: action.payload.checked,
      }));
      break;
    case ACTION_TYPES.SET_TG_VALUE:
      newState.tg = snapTo(action.payload, state.uniqueTgValues);
      break;
    case ACTION_TYPES.SET_TD_VALUE:
      newState.td = snapTo(action.payload, state.uniqueTdValues);
      break;
    case ACTION_TYPES.SET_DK_VALUE:
      newState.dk = round(action.payload, 2);
      break;
    case ACTION_TYPES.SET_DF_VALUE:
      newState.df = snapTo(action.payload, state.uniqueDfValues);
      break;
  }

  const searchConfig = getSearchConfig(newState);

  if (searchConfig.$and) {
    newState.searchResults = fuse.search(searchConfig);
  } else {
    newState.searchResults = initialState.searchResults;
  }

  newState.searchResults = newState.searchResults.filter((r) => {
    const product = r.item || r;
    // ex: df: "0.0028 - 0.0035"
    // ex: dk: "3.38 / 3.45 / 3.60 / 3.75"
    // value <= dk & df
    // value >= tg & td
    const { tg, td, dk, df } = newState;
    const dfs = parseDf(product.df);
    const maxDf = Math.max(...dfs);
    const minDf = Math.min(...dfs);

    const dks = parseDk(product.dk);
    const maxDk = Math.max(...dks);
    const minDk = Math.min(...dks);

    return (
      (maxDf <= df || minDf <= df) &&
      (maxDk <= dk || minDk <= dk) &&
      parseFloat(product.tg) >= tg &&
      parseFloat(product.td) >= td
    );
  });

  return newState;
}

const Menu = ({ search, activeTab = "All Products" }) => {
  const [state, dispatch] = useReducer(
    reducer,
    {
      ...initialState,
      primaryMenu: findIndex(primaryMenuItems, { title: activeTab }) || 0,
      searchText: search,
    },
    init
  );

  const buttonHoverClasses =
    "text-theme-gray hover:from-theme-orange-grad hover:to-transparent hover:border-theme-orange hover:text-white";
  const buttonActiveClasses =
    "from-theme-orange-grad to-transparent border-theme-orange text-white";

  return (
    <div>
      <div className="flex flex-col lg:flex-row ">
        <div className="bg-gray-800 border-l-6 border-gray-800 flex-shrink-0 pb-6 pt-4 lg:pt-10 xl:pl-12 pr-4 lg:pr-0">
          <h2 className="text-2xl px-6 lg:px-12 border-l-6 border-gray-800 text-white mb-2 lg:mb-6">
            Our Products
          </h2>
          <p className="font-bold  px-6 lg:px-12 border-l-6 border-gray-800 text-white">
            Filter By:
          </p>

          <div className="px-6 lg:px-12 border-l-6 border-gray-800">
            <select
              className="block lg:hidden w-full p-2"
              onChange={(e) => {
                dispatch({
                  type: ACTION_TYPES.SET_PRIMARY_MENU,
                  payload: parseInt(e.target.value),
                });
                dispatch({
                  type: ACTION_TYPES.SET_SECONDARY_MENU,
                  payload: 0,
                });
                dispatch({ type: ACTION_TYPES.RESET_SEARCH_RESULTS });
              }}
            >
              {primaryMenuItems.map((item, idx) => {
                return (
                  <option
                    key={item.title}
                    value={idx}
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                );
              })}
            </select>
          </div>

          <ul className="hidden lg:block">
            {primaryMenuItems.map((item, idx) => {
              const isActive = idx === state.primaryMenu;
              return (
                <li key={item.title}>
                  <button
                    onClick={() => {
                      dispatch({
                        type: ACTION_TYPES.SET_PRIMARY_MENU,
                        payload: idx,
                      });
                      dispatch({
                        type: ACTION_TYPES.SET_SECONDARY_MENU,
                        payload: 0,
                      });
                      dispatch({ type: ACTION_TYPES.RESET_SEARCH_RESULTS });
                    }}
                    className={`block w-full text-left focus:outline-none leading-10 border-l-6 border-gray-800 pl-12 bg-gradient-to-r ${
                      isActive ? buttonActiveClasses : buttonHoverClasses
                    }`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: item.title }} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {primaryMenuItems[state.primaryMenu].facets && (
          <div className="bg-gray-300 pt-8 px-4 flex-shrink-0">
            <p className="font-bold leading-8 mb-2">
              {primaryMenuItems[state.primaryMenu].title}
            </p>

            <div className="mb-10 px-6 lg:px-12 lg:hidden">
              <select
                className="block w-full p-2"
                onChange={(e) => {
                  const idx = e.target.value;
                  const facet = primaryMenuItems[state.primaryMenu].facets[idx];
                  const searchKey =
                    facet.post_type === "market"
                      ? "markets.slug"
                      : "attributes.slug";
                  dispatch({
                    type: ACTION_TYPES.SET_SECONDARY_MENU,
                    payload: facet.id,
                  });
                  dispatch({
                    type: ACTION_TYPES.SET_SEARCH_RESULTS,
                    payload: fuse.search({ [searchKey]: `=${facet.slug}` }),
                  });
                }}
              >
                {primaryMenuItems[state.primaryMenu].facets.map(
                  (facet, idx) => {
                    return (
                      <option
                        key={facet.id}
                        value={idx}
                        dangerouslySetInnerHTML={{ __html: facet.title }}
                      />
                    );
                  }
                )}
              </select>
            </div>

            <ul className="hidden lg:block">
              {primaryMenuItems[state.primaryMenu].facets.map((facet) => {
                const selected = state.secondaryMenu === facet.id;
                const searchKey =
                  facet.post_type === "market"
                    ? "markets.slug"
                    : "attributes.slug";
                return (
                  <li key={facet.id}>
                    <button
                      onClick={() => {
                        dispatch({
                          type: ACTION_TYPES.SET_SECONDARY_MENU,
                          payload: facet.id,
                        });
                        dispatch({
                          type: ACTION_TYPES.SET_SEARCH_RESULTS,
                          payload: fuse.search({
                            [searchKey]: `=${facet.slug}`,
                          }),
                        });
                      }}
                      className={`${
                        selected ? "border-b border-gray-500" : ""
                      } focus:outline-none block w-full text-theme-gray text-sm font-medium leading-8`}
                    >
                      {facet.icon_text && (
                        <img
                          className={`h-16 w-auto mb-2`}
                          src={facet.icon_text}
                          alt={facet.title}
                        />
                      )}
                      <span
                        className={`${facet.icon_text ? "hidden" : ""}`}
                        dangerouslySetInnerHTML={{ __html: facet.title }}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="bg-ultra-light-gray flex-initial flex-grow relative product-list-wrap">
          {state.primaryMenu === 3 && (
            <AdvancedSearchForm
              searchText={state.searchText}
              dispatch={dispatch}
              attributes={state.attributes}
              markets={state.markets}
              regions={state.regions}
              slashsheets={state.slashsheets}
              tg={state.tg}
              td={state.td}
              dk={state.dk}
              df={state.df}
              uniqueTgValues={state.uniqueTgValues}
              uniqueTdValues={state.uniqueTdValues}
              uniqueDkValues={state.uniqueDkValues}
              uniqueDfValues={state.uniqueDfValues}
            />
          )}

          <SearchResults
            products={state.searchResults}
            primaryMenu={state.primaryMenu}
          />
        </div>
      </div>
    </div>
  );
};

export default hot(module)(Menu);
